import React, { useEffect, useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';

import { ChevronRight, Printer } from 'lucide-react';

import EndDryingDrawer from '@/components/drying-lots/EndDryingDrawer';
import { Card } from '@/components/shadcn-ui/card';
import { cn } from '@/lib/utils';
import { useLotStore } from '@/stores/useLot.store';
import AddDryingBedDrawer from '@/components/drying-lots/AddDryingBedDrawer';
import { getDryingBedsOfLot } from '@/network/api.service';
import Loader from '@/components/sharedComponents/Loader';
import { generateAllBedPdfs } from '@/lib/generate-bed-pdf';
import { useMasterStore } from '@/stores/useMaster.store';

const formatBedCode = (bed_number, bed_position) => {
  const prefix = bed_position === 'upper' ? 'U' : bed_position === 'middle' ? 'M' : 'L';
  return `${prefix}${String(bed_number).padStart(2, '0')}`;
};

export const formatDateTimeUTC = isoString => {
  const date = new Date(isoString);

  const datePart = date.toLocaleDateString('en-CA'); // YYYY-MM-DD
  const timePart = date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });

  return `${datePart} ${timePart}`;
};

const DryingInProgressPage = () => {
  const location = useLocation();
  const fromScreen = location.state?.from || 'unknown';

  const [loading, setLoading] = useState(false);
  const [section, setSection] = useState('Active'); // Active or Lapsed
  const [lotBeds, setLotBeds] = useState([]);

  const { lot_id } = useParams();
  const fetchLot = useLotStore(state => state.fetchLot);
  const lot = useLotStore(state => state.lot);
  const fetchDryingBeds = useMasterStore(state => state.fetchDryingBeds);

  // const dryingBeds = useMasterStore(state => state.dryingBeds);

  const fetchLotData = async () => {
    await fetchLot(lot_id, false);

    const { data } = await getDryingBedsOfLot(lot_id);
    setLotBeds(data?.data);
  };

  const handleGenerateAllBedPdfs = async () => {
    await generateAllBedPdfs(lotBeds, lot?.estate?.name, lot?.date_of_harvest, lot?.drying_lot?.drying_start_time);
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        await fetchLotData();
        await fetchDryingBeds();
      } catch (error) {
        console.error('Error loading data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [lot_id]);

  if (loading) {
    return (
      <div className="flex h-full items-center justify-center">
        <Loader />
      </div>
    );
  }
  return (
    <div className="flex h-full flex-1 flex-col space-y-[18px] px-4 py-[18px] pb-2">
      {/* Lot Information */}
      <Card className="flex flex-col gap-1 p-4">
        <div className="flex items-center justify-between">
          <div className="mb-1 text-base font-semibold">Lot Information</div>
          <button className="mr-1" onClick={handleGenerateAllBedPdfs}>
            <Printer size={20} />
          </button>
        </div>
        <div className="text-sm">
          <span className="font-semibold">Lot:</span> {lot?.lotname}
        </div>
        <div className="text-sm">
          <span className="font-semibold">Estate:</span> {lot?.estate?.name}
        </div>
        <div className="text-sm">
          <span className="font-semibold">Drying start date:</span>{' '}
          {formatDateTimeUTC(lot?.drying_lot?.drying_start_time || new Date())}
        </div>
        <div className="text-sm">
          <span className="font-semibold">Drying start weight:</span> {lot?.drying_lot?.drying_start_weight_kg} kg
        </div>
      </Card>

      {/* Drying Section */}
      <div className="flex min-h-0 flex-1 flex-col">
        {/* Tabs */}
        <Card className="flex h-[50px] items-center rounded-[8px] px-1 py-1.5">
          <button
            className={cn('h-full flex-1 rounded-[8px] font-medium', section === 'Active' && 'bg-[#F3F4F6]')}
            onClick={() => setSection('Active')}
          >
            Active
          </button>
          <button
            className={cn('h-full flex-1 rounded-[8px] font-medium', section === 'Lapsed' && 'bg-[#F3F4F6]')}
            onClick={() => setSection('Lapsed')}
          >
            Lapsed
          </button>
        </Card>

        {/* Scrollable List ONLY */}
        <div className="mt-3 flex-1 overflow-y-auto">
          {section === 'Active' && (
            <div className="flex flex-col gap-2 pr-1">
              {lotBeds
                ?.filter(item => item?.drying_status === true)
                .map(item => (
                  <Link key={item?._id} to={`/drying-lots/in-progress/${lot_id}/bed/${item?._id}`}>
                    <Card className="flex items-center justify-between p-3">
                      <div className="flex flex-1 flex-col">
                        <div className="font-medium">{formatBedCode(item?.bed_number, item?.bed_position)}</div>
                        <div className="text-sm">Drying since {item?.drying_days} days</div>
                        {item?.dryingChecks?.length > 0 && (
                          <div className="text-sm">Moisture: {item?.dryingChecks?.[0]?.moisture} %</div>
                        )}
                        <div className="text-sm">
                          {/* Last check: {item?. ? new Date(item?.last_check_time).toLocaleString() : 'N/A'} */}
                          Last check:{' '}
                          {item?.dryingChecks[0]?.time_of_check
                            ? formatDateTimeUTC(item?.dryingChecks[0]?.time_of_check)
                            : 'N/A'}
                        </div>
                      </div>
                      <button className="mr-1 flex-shrink-0">
                        <ChevronRight className="h-5 w-5" />
                      </button>
                    </Card>
                  </Link>
                ))}
            </div>
          )}
          {section === 'Lapsed' && (
            <div className="flex flex-col gap-2 pr-1">
              {lotBeds
                ?.filter(item => item?.drying_status === false)
                .map(item => (
                  <Link key={item?._id} to={`/drying-lots/in-progress/${lot_id}/bed/${item?._id}`}>
                    <Card className="flex items-center justify-between p-3">
                      <div className="flex flex-1 flex-col">
                        <div className="font-medium">{formatBedCode(item?.bed_number, item?.bed_position)}</div>
                        <div className="text-sm">Drying since {item?.drying_days} days</div>
                        {item?.dryingChecks?.length > 0 &&
                          (item?.dryingChecks?.[0]?.moisture !== null ? (
                            <div className="text-sm">Moisture: {item?.dryingChecks?.[0]?.moisture} %</div>
                          ) : (
                            <div className="text-sm">Moisture: N/A</div>
                          ))}
                        <div className="text-sm">
                          {/* Last check: {item?. ? new Date(item?.last_check_time).toLocaleString() : 'N/A'} */}
                          Last check:{' '}
                          {item?.dryingChecks[0]?.time_of_check
                            ? formatDateTimeUTC(item?.dryingChecks[0]?.time_of_check)
                            : 'N/A'}
                        </div>
                      </div>
                      <button className="mr-1 flex-shrink-0">
                        <ChevronRight className="h-5 w-5" />
                      </button>
                    </Card>
                  </Link>
                ))}
            </div>
          )}
        </div>

        {/* Fixed Bottom Button */}
        <div className="mt-3 flex flex-col gap-2">
          <AddDryingBedDrawer dryingLots={lotBeds} fetchLotData={fetchLotData} stage={lot?.stage} />
          {/* Below button will be available when all the checks status are completed */}
          <EndDryingDrawer
            stage={lot?.stage}
            disabled={!lotBeds?.every(bed => bed?.drying_status === false)}
            lotId={lot_id}
            fromScreen={fromScreen}
          />
        </div>
      </div>
    </div>
  );
};

export default DryingInProgressPage;
